/**
 * Connection Model Constants
 *
 * Simplified connection types for "One Generation to Next":
 * - Connected: Adults with accounts who can see all content
 * - Connected (Minor): Children connected through parent accounts
 * - Tree Only: People in family tree but cannot see content
 * - Pending: Invitations sent but not yet accepted
 */

// Connection type constants
export const CONNECTION_TYPES = {
  CONNECTED: 'connected',
  MINOR: 'minor',
  PENDING: 'pending',
  TREE_ONLY: 'tree_only',
  NONE: 'none'
}

// Connection type labels for UI display
export const CONNECTION_LABELS = {
  [CONNECTION_TYPES.CONNECTED]: 'Connected',
  [CONNECTION_TYPES.MINOR]: 'Connected (Minor)',
  [CONNECTION_TYPES.PENDING]: 'Invitation Pending',
  [CONNECTION_TYPES.TREE_ONLY]: 'Tree Only',
  [CONNECTION_TYPES.NONE]: 'Not Connected'
}

// Connection type descriptions
export const CONNECTION_DESCRIPTIONS = {
  [CONNECTION_TYPES.CONNECTED]: 'Full access to family stories and content',
  [CONNECTION_TYPES.MINOR]: 'Access through parent\'s account',
  [CONNECTION_TYPES.PENDING]: 'Waiting for invitation acceptance',
  [CONNECTION_TYPES.TREE_ONLY]: 'Documented in tree, no content access',
  [CONNECTION_TYPES.NONE]: 'No connection'
}

// Icons/Emojis for connection types
export const CONNECTION_ICONS = {
  [CONNECTION_TYPES.CONNECTED]: '✅',
  [CONNECTION_TYPES.MINOR]: '👶',
  [CONNECTION_TYPES.PENDING]: '⏳',
  [CONNECTION_TYPES.TREE_ONLY]: '🌳',
  [CONNECTION_TYPES.NONE]: '❌'
}

// Colors for connection badges
export const CONNECTION_COLORS = {
  [CONNECTION_TYPES.CONNECTED]: 'bg-green-100 text-green-800 border-green-300',
  [CONNECTION_TYPES.MINOR]: 'bg-blue-100 text-blue-800 border-blue-300',
  [CONNECTION_TYPES.PENDING]: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  [CONNECTION_TYPES.TREE_ONLY]: 'bg-gray-100 text-gray-800 border-gray-300',
  [CONNECTION_TYPES.NONE]: 'bg-red-100 text-red-800 border-red-300'
}

/**
 * Helper Functions
 */

/**
 * Check if a connection type can view content
 * @param {string} connectionType
 * @returns {boolean}
 */
export function canViewContent(connectionType) {
  return [
    CONNECTION_TYPES.CONNECTED,
    CONNECTION_TYPES.MINOR
  ].includes(connectionType)
}

/**
 * Get display label for connection type
 * @param {string} connectionType
 * @returns {string}
 */
export function getConnectionLabel(connectionType) {
  return CONNECTION_LABELS[connectionType] || 'Unknown'
}

/**
 * Get icon for connection type
 * @param {string} connectionType
 * @returns {string}
 */
export function getConnectionIcon(connectionType) {
  return CONNECTION_ICONS[connectionType] || ''
}

/**
 * Get color classes for connection badge
 * @param {string} connectionType
 * @returns {string}
 */
export function getConnectionColor(connectionType) {
  return CONNECTION_COLORS[connectionType] || CONNECTION_COLORS[CONNECTION_TYPES.NONE]
}

/**
 * Group connections by type
 * @param {Array} connections - Array of connection objects
 * @returns {Object} Grouped connections
 */
export function groupConnectionsByType(connections) {
  const grouped = {
    [CONNECTION_TYPES.CONNECTED]: [],
    [CONNECTION_TYPES.MINOR]: [],
    [CONNECTION_TYPES.PENDING]: [],
    [CONNECTION_TYPES.TREE_ONLY]: []
  }

  connections.forEach(conn => {
    const type = conn.connection_type
    if (grouped[type]) {
      grouped[type].push(conn)
    }
  })

  return grouped
}

/**
 * Calculate connection summary statistics
 * @param {Array} connections - Array of connection objects
 * @returns {Object} Summary statistics
 */
export function getConnectionSummary(connections) {
  const grouped = groupConnectionsByType(connections)

  return {
    total_can_view: grouped[CONNECTION_TYPES.CONNECTED].length + grouped[CONNECTION_TYPES.MINOR].length,
    adults: grouped[CONNECTION_TYPES.CONNECTED].length,
    minors: grouped[CONNECTION_TYPES.MINOR].length,
    pending: grouped[CONNECTION_TYPES.PENDING].length,
    tree_only: grouped[CONNECTION_TYPES.TREE_ONLY].length
  }
}

/**
 * Format guardian display text
 * @param {string} guardianName
 * @returns {string}
 */
export function formatGuardianDisplay(guardianName) {
  return guardianName ? `via ${guardianName}` : ''
}
